import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import {
  RiBankLine,
  RiAddLine,
  RiSearchLine,
  RiCloseLine,
  RiEditLine,
  RiDeleteBinLine,
  RiCheckLine,
  RiErrorWarningLine,
  RiRefreshLine,
  RiMailLine,
  RiPhoneLine,
  RiMapPinLine,
  RiUserLine,
  RiBuilding2Line,
  RiHashtag,
  RiArrowDownSLine,
  RiGitBranchLine,
} from "react-icons/ri";
import { AiOutlineLoading3Quarters } from "react-icons/ai";

const BANK_BASE_URL = `${process.env.REACT_APP_API_URL}/bank`;
const BRANCH_BASE_URL = `${process.env.REACT_APP_API_URL}/branch`;

const defaultForm = {
  branchName: "",
  address: "",
  pin: "",
  email: "",
  phone: "",
  contactPerson: "",
  bankId: "",
};

/* ─── Spinner ─── */
const Spinner = () => (
  <AiOutlineLoading3Quarters className="animate-spin w-4 h-4" />
);

/* ─── Toast ─── */
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, []);
  return (
    <div
      className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3 rounded-xl shadow-xl text-sm font-medium
      ${
        type === "success"
          ? "bg-emerald-50 border border-emerald-200 text-emerald-800"
          : "bg-red-50 border border-red-200 text-red-800"
      }`}
    >
      {type === "success" ? (
        <RiCheckLine className="w-4 h-4 text-emerald-500" />
      ) : (
        <RiErrorWarningLine className="w-4 h-4 text-red-500" />
      )}
      <span>{message}</span>
      <button
        onClick={onClose}
        className="ml-2 opacity-50 hover:opacity-100 transition"
      >
        <RiCloseLine className="w-4 h-4" />
      </button>
    </div>
  );
};

/* ─── Modal ─── */
const Modal = ({ title, onClose, children }) =>
  createPortal(
    <div className="fixed inset-0 z-40 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-blue-950/40 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative z-10 w-full max-w-2xl mx-4 bg-white border border-blue-100 rounded-2xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-blue-100 bg-blue-50">
          <h2 className="text-base font-semibold text-blue-900 flex items-center gap-2">
            {title}
          </h2>
          <button
            onClick={onClose}
            className="text-blue-400 hover:text-blue-700 transition rounded-lg p-1 hover:bg-blue-100"
          >
            <RiCloseLine className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 max-h-[80vh] overflow-y-auto bg-white">
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );

/* ─── Field ─── */
const iconMap = {
  branchName: RiGitBranchLine,
  address: RiMapPinLine,
  pin: RiHashtag,
  email: RiMailLine,
  phone: RiPhoneLine,
  contactPerson: RiUserLine,
};

const Field = ({ label, name, value, onChange, type = "text" }) => {
  const Icon = iconMap[name] || RiBuilding2Line;
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
        {label}
      </label>
      <div className="relative">
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300" />
        <input
          type={type}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={label}
          className="w-full bg-blue-50/60 border border-blue-200 rounded-lg pl-9 pr-3 py-2.5 text-sm text-slate-800 placeholder-blue-300 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition"
        />
      </div>
    </div>
  );
};

/* ─── Bank Dropdown (reusable) ─── */
const BankDropdown = ({
  banks,
  value,
  onChange,
  loading,
  placeholder = "Select a Bank…",
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const selected = banks.find((b) => b._id === value) || null;

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={loading}
        className="w-full flex items-center gap-3 bg-blue-50/60 border border-blue-200 rounded-lg px-3 py-2.5 text-sm text-slate-800 hover:border-blue-400 transition focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
      >
        {selected ? (
          <>
            {selected.bankLogo ? (
              <img
                src={selected.bankLogo}
                alt={selected.bankName}
                className="w-5 h-5 rounded object-contain border border-blue-100 flex-shrink-0"
              />
            ) : (
              <div className="w-5 h-5 rounded bg-blue-600 flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0">
                {(selected.bankName || "?")[0]}
              </div>
            )}
            <span className="font-medium text-slate-800 truncate">
              {selected.bankName}
            </span>
          </>
        ) : (
          <>
            <RiBankLine className="w-4 h-4 text-blue-300 flex-shrink-0" />
            <span className="text-blue-300">{placeholder}</span>
          </>
        )}
        <RiArrowDownSLine
          className={`w-4 h-4 text-blue-400 ml-auto flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-1 w-full bg-white border border-blue-100 rounded-xl shadow-xl z-50 overflow-hidden">
          <div className="max-h-52 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-6 text-blue-400 gap-2">
                <AiOutlineLoading3Quarters className="w-4 h-4 animate-spin" />
                <span className="text-xs">Loading…</span>
              </div>
            ) : banks.length === 0 ? (
              <div className="text-center py-6 text-xs text-slate-400">
                No banks available
              </div>
            ) : (
              banks.map((bank) => (
                <button
                  key={bank._id}
                  type="button"
                  onClick={() => {
                    onChange(bank._id);
                    setOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-blue-50 transition text-left ${
                    value === bank._id
                      ? "bg-blue-50 text-blue-700 font-semibold"
                      : "text-slate-700"
                  }`}
                >
                  {bank.bankLogo ? (
                    <img
                      src={bank.bankLogo}
                      alt={bank.bankName}
                      className="w-6 h-6 rounded object-contain border border-blue-100 flex-shrink-0"
                    />
                  ) : (
                    <div className="w-6 h-6 rounded bg-blue-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                      {(bank.bankName || "?")[0]}
                    </div>
                  )}
                  <span className="truncate">{bank.bankName}</span>
                  {value === bank._id && (
                    <RiCheckLine className="w-4 h-4 text-blue-500 ml-auto flex-shrink-0" />
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── Header Bank Filter Selector ─── */
const BankFilterSelector = ({ banks, selected, onSelect, loading }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        disabled={loading}
        className="flex items-center gap-3 bg-white border border-blue-200 rounded-xl px-4 py-2.5 text-sm text-slate-700 hover:border-blue-400 hover:bg-blue-50/50 transition shadow-sm min-w-[220px] max-w-xs"
      >
        {selected ? (
          <>
            {selected.bankLogo ? (
              <img
                src={selected.bankLogo}
                alt={selected.bankName}
                className="w-6 h-6 rounded object-contain border border-blue-100"
              />
            ) : (
              <div className="w-6 h-6 rounded bg-blue-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                {(selected.bankName || "?")[0]}
              </div>
            )}
            <span className="font-semibold text-blue-800 truncate">
              {selected.bankName}
            </span>
          </>
        ) : (
          <>
            <RiBankLine className="w-4 h-4 text-blue-400 flex-shrink-0" />
            <span className="text-blue-400">All Banks</span>
          </>
        )}
        <RiArrowDownSLine
          className={`w-4 h-4 text-blue-400 ml-auto flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div className="absolute top-full left-0 mt-2 w-72 bg-white border border-blue-100 rounded-xl shadow-xl z-30 overflow-hidden">
          <div className="px-3 py-2 border-b border-blue-50 text-[11px] font-semibold text-blue-400 uppercase tracking-widest">
            Filter by Bank
          </div>
          <div className="max-h-64 overflow-y-auto">
            {/* All Banks option */}
            <button
              onClick={() => {
                onSelect(null);
                setOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-blue-50 transition text-left ${
                !selected
                  ? "bg-blue-50 text-blue-700 font-semibold"
                  : "text-slate-700"
              }`}
            >
              <div className="w-7 h-7 rounded bg-blue-100 flex items-center justify-center flex-shrink-0">
                <RiBankLine className="w-4 h-4 text-blue-500" />
              </div>
              <span>All Banks</span>
              {!selected && (
                <RiCheckLine className="w-4 h-4 text-blue-500 ml-auto flex-shrink-0" />
              )}
            </button>

            {loading ? (
              <div className="flex items-center justify-center py-8 text-blue-400 gap-2">
                <AiOutlineLoading3Quarters className="w-4 h-4 animate-spin" />
                <span className="text-xs">Loading…</span>
              </div>
            ) : banks.length === 0 ? (
              <div className="text-center py-8 text-xs text-slate-400">
                No banks available
              </div>
            ) : (
              banks.map((bank) => (
                <button
                  key={bank._id}
                  onClick={() => {
                    onSelect(bank);
                    setOpen(false);
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-blue-50 transition text-left ${
                    selected?._id === bank._id
                      ? "bg-blue-50 text-blue-700 font-semibold"
                      : "text-slate-700"
                  }`}
                >
                  {bank.bankLogo ? (
                    <img
                      src={bank.bankLogo}
                      alt={bank.bankName}
                      className="w-7 h-7 rounded object-contain border border-blue-100 flex-shrink-0"
                    />
                  ) : (
                    <div className="w-7 h-7 rounded bg-blue-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                      {(bank.bankName || "?")[0]}
                    </div>
                  )}
                  <div className="flex flex-col min-w-0">
                    <span className="truncate font-medium">
                      {bank.bankName}
                    </span>
                    {bank.headOffice && (
                      <span className="text-[11px] text-slate-400 truncate">
                        {bank.headOffice}
                      </span>
                    )}
                  </div>
                  {selected?._id === bank._id && (
                    <RiCheckLine className="w-4 h-4 text-blue-500 ml-auto flex-shrink-0" />
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const formFields = [
  { label: "Branch Name", name: "branchName" },
  { label: "Manager Name", name: "contactPerson" },
  { label: "Phone Number", name: "phone", type: "tel" },
  { label: "Email", name: "email", type: "email" },
  { label: "Address", name: "address" },
  { label: "PIN", name: "pin" },
];

const tableHeaders = [
  { label: "Branch Name", Icon: RiGitBranchLine },
  { label: "Bank", Icon: RiBankLine },
  { label: "Manager", Icon: RiUserLine },
  { label: "Email", Icon: RiMailLine },
  { label: "Phone", Icon: RiPhoneLine },
  { label: "Address", Icon: RiMapPinLine },
  { label: "PIN", Icon: RiHashtag },
  { label: "Actions" },
];

export default function SubClient() {
  const [banks, setBanks] = useState([]);
  const [banksLoading, setBanksLoading] = useState(false);
  const [filterBank, setFilterBank] = useState(null); // for header filter

  const [branches, setBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [toast, setToast] = useState(null);
  const [search, setSearch] = useState("");

  const notify = (message, type = "success") => setToast({ message, type });

  /* ── Fetch all banks ── */
  const fetchBanks = async () => {
    setBanksLoading(true);
    try {
      const res = await axios.get(`${BANK_BASE_URL}/get-banks`);
      setBanks(res.data?.data || res.data || []);
    } catch {
      notify("Failed to fetch banks", "error");
    } finally {
      setBanksLoading(false);
    }
  };

  /* ── Fetch ALL branches ── */
  const fetchBranches = async () => {
    setBranchesLoading(true);
    try {
      const res = await axios.get(`${BRANCH_BASE_URL}/get-branches`);
      setBranches(res.data?.data || res.data || []);
    } catch {
      notify("Failed to fetch branches", "error");
    } finally {
      setBranchesLoading(false);
    }
  };

  useEffect(() => {
    fetchBanks();
    fetchBranches();
  }, []);

  /* ── Resolve bank name for a branch (bankId may be populated object or string) ── */
  const resolveBankName = (branch) => {
    if (branch.bankId && typeof branch.bankId === "object") {
      return branch.bankId.bankName || "—";
    }
    const found = banks.find((b) => b._id === branch.bankId);
    return found?.bankName || "—";
  };

  const resolveBankId = (branch) => {
    if (branch.bankId && typeof branch.bankId === "object")
      return branch.bankId._id;
    return branch.bankId || "";
  };

  /* ── Modal helpers ── */
  const openCreate = () => {
    setForm(defaultForm);
    setModal("create");
  };
  const openEdit = (branch) => {
    setSelected(branch);
    setForm({
      branchName: branch.branchName || "",
      address: branch.address || "",
      pin: branch.pin || "",
      email: branch.email || "",
      phone: branch.phone || "",
      contactPerson: branch.contactPerson || "",
      bankId: resolveBankId(branch),
    });
    setModal("edit");
  };
  const openDelete = (branch) => {
    setSelected(branch);
    setModal("delete");
  };
  const closeModal = () => {
    setModal(null);
    setSelected(null);
  };
  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  /* ── CRUD ── */
  const handleCreate = async () => {
    if (!form.bankId) return notify("Please select a bank", "error");
    setActionLoading(true);
    try {
      await axios.post(`${BRANCH_BASE_URL}/create-branch`, form);
      notify("Branch created successfully");
      fetchBranches();
      closeModal();
    } catch {
      notify("Failed to create branch", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdate = async () => {
    if (!form.bankId) return notify("Please select a bank", "error");
    setActionLoading(true);
    try {
      await axios.put(`${BRANCH_BASE_URL}/update-branch/${selected._id}`, form);
      notify("Branch updated successfully");
      fetchBranches();
      closeModal();
    } catch {
      notify("Failed to update branch", "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    setActionLoading(true);
    try {
      await axios.delete(`${BRANCH_BASE_URL}/delete-branch/${selected._id}`);
      notify("Branch deleted successfully");
      fetchBranches();
      closeModal();
    } catch {
      notify("Failed to delete branch", "error");
    } finally {
      setActionLoading(false);
    }
  };

  /* ── Filtered list ── */
  const filtered = branches.filter((b) => {
    const bankName = resolveBankName(b).toLowerCase();
    const matchSearch =
      (b.branchName || "").toLowerCase().includes(search.toLowerCase()) ||
      (b.contactPerson || "").toLowerCase().includes(search.toLowerCase()) ||
      (b.email || "").toLowerCase().includes(search.toLowerCase()) ||
      bankName.includes(search.toLowerCase());
    const matchBank = !filterBank || resolveBankId(b) === filterBank._id;
    return matchSearch && matchBank;
  });

  return (
    <div className="min-h-screen bg-white text-slate-800">
      {/* Header */}
      <header className="bg-white border-b border-blue-100 sticky top-0 z-30 shadow-sm">
        <div className="w-full mx-auto px-6 py-3.5 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center text-white shadow-md shadow-blue-200">
              <RiGitBranchLine className="w-5 h-5" />
            </div>
            <div>
              <h1 className="text-base font-bold text-blue-800 tracking-tight">
                Branch Management
              </h1>
              <p className="text-[11px] text-blue-400 tracking-widest uppercase">
                Sub-Client Branches
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {/* Bank Filter */}
            <BankFilterSelector
              banks={banks}
              selected={filterBank}
              onSelect={(bank) => {
                setFilterBank(bank);
                setSearch("");
              }}
              loading={banksLoading}
            />

            {/* Search */}
            <div className="relative">
              <RiSearchLine className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search branches..."
                className="bg-blue-50 border border-blue-200 rounded-lg pl-9 pr-3 py-2 text-sm text-slate-700 placeholder-blue-300 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 w-48 transition"
              />
            </div>

            {/* Refresh */}
            <button
              onClick={fetchBranches}
              disabled={branchesLoading}
              title="Refresh"
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-blue-200 text-blue-400 hover:text-blue-600 hover:bg-blue-50 transition disabled:opacity-50"
            >
              <RiRefreshLine
                className={`w-4 h-4 ${branchesLoading ? "animate-spin" : ""}`}
              />
            </button>

            {/* Add Branch */}
            <button
              onClick={openCreate}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-sm px-4 py-2 rounded-lg transition-all shadow-md shadow-blue-200"
            >
              <RiAddLine className="w-4 h-4" />
              Add Branch
            </button>
          </div>
        </div>
      </header>

      <div className="w-full mx-auto px-6 pt-6 pb-10">
        {/* Summary pill */}
        <div className="flex items-center gap-3 mb-5 bg-blue-50/70 border border-blue-100 rounded-xl px-4 py-3 w-fit">
          {filterBank ? (
            <>
              {filterBank.bankLogo ? (
                <img
                  src={filterBank.bankLogo}
                  alt={filterBank.bankName}
                  className="w-8 h-8 rounded object-contain border border-blue-100"
                />
              ) : (
                <div className="w-8 h-8 rounded bg-blue-600 flex items-center justify-center text-white text-sm font-bold">
                  {(filterBank.bankName || "?")[0]}
                </div>
              )}
              <p className="text-sm font-bold text-blue-800">
                {filterBank.bankName}
              </p>
            </>
          ) : (
            <>
              <div className="w-8 h-8 rounded bg-blue-100 flex items-center justify-center">
                <RiBankLine className="w-4 h-4 text-blue-500" />
              </div>
              <p className="text-sm font-bold text-blue-800">All Banks</p>
            </>
          )}
          <span className="ml-2 bg-blue-100 text-blue-600 text-[11px] font-semibold px-2.5 py-0.5 rounded-full">
            {filtered.length} branch{filtered.length !== 1 ? "es" : ""}
          </span>
        </div>

        {/* Table */}
        <div className="bg-white border border-blue-100 rounded-2xl overflow-hidden shadow-sm">
          <div className="px-5 py-3.5 border-b border-blue-50 bg-blue-50/50 flex items-center justify-between">
            <span className="text-sm font-semibold text-blue-800 flex items-center gap-2">
              <RiGitBranchLine className="w-4 h-4 text-blue-500" />
              {filterBank
                ? `Branches — ${filterBank.bankName}`
                : "All Branches"}
            </span>
            <span className="text-xs text-blue-400">
              {filtered.length} record{filtered.length !== 1 ? "s" : ""}
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-blue-100 bg-blue-50/30">
                  {tableHeaders.map(({ label, Icon }) => (
                    <th key={label} className="px-4 py-3 text-left">
                      <span className="flex items-center gap-1.5 text-xs font-semibold text-blue-500 uppercase tracking-widest whitespace-nowrap">
                        {Icon && <Icon className="w-3.5 h-3.5" />}
                        {label}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {branchesLoading ? (
                  <tr>
                    <td colSpan={8} className="py-20 text-center">
                      <div className="flex flex-col items-center gap-3 text-blue-400">
                        <AiOutlineLoading3Quarters className="w-7 h-7 animate-spin text-blue-500" />
                        <span className="text-sm">Loading branches…</span>
                      </div>
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="py-20 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <div className="w-14 h-14 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center">
                          <RiGitBranchLine className="w-7 h-7 text-blue-300" />
                        </div>
                        <span className="text-sm text-slate-400">
                          {search || filterBank
                            ? "No branches match your filter."
                            : "No branches yet. Add one to get started."}
                        </span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filtered.map((branch, i) => (
                    <tr
                      key={branch._id || i}
                      className="border-b border-blue-50 hover:bg-blue-50/50 transition group"
                    >
                      <td className="px-4 py-3 font-semibold text-slate-800 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center">
                            <RiGitBranchLine className="w-3.5 h-3.5 text-blue-500" />
                          </div>
                          {branch.branchName || "—"}
                        </div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="inline-flex items-center gap-1.5 bg-blue-50 border border-blue-100 text-blue-700 text-xs font-semibold px-2.5 py-1 rounded-lg">
                          <RiBankLine className="w-3 h-3" />
                          {resolveBankName(branch)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                        {branch.contactPerson || "—"}
                      </td>
                      <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                        {branch.email || "—"}
                      </td>
                      <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                        {branch.phone || "—"}
                      </td>
                      <td className="px-4 py-3 text-slate-500 max-w-[180px] truncate">
                        {branch.address || "—"}
                      </td>
                      <td className="px-4 py-3 text-slate-500">
                        {branch.pin || "—"}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button
                            onClick={() => openEdit(branch)}
                            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-blue-50 border border-blue-200 text-blue-600 hover:bg-blue-100 font-semibold transition"
                          >
                            <RiEditLine className="w-3.5 h-3.5" /> Edit
                          </button>
                          <button
                            onClick={() => openDelete(branch)}
                            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-red-50 border border-red-200 text-red-500 hover:bg-red-100 font-semibold transition"
                          >
                            <RiDeleteBinLine className="w-3.5 h-3.5" /> Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* ── Create Modal ── */}
      {modal === "create" && (
        <Modal
          title={
            <>
              <RiAddLine className="w-5 h-5 text-blue-500" /> Add New Branch
            </>
          }
          onClose={closeModal}
        >
          {/* Bank selector at top */}
          <div className="flex flex-col gap-1.5 mb-4">
            <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
              Select Bank
            </label>
            <BankDropdown
              banks={banks}
              value={form.bankId}
              onChange={(id) => setForm((f) => ({ ...f, bankId: id }))}
              loading={banksLoading}
            />
          </div>

          <div className="grid grid-cols-2 gap-4 mb-6">
            {formFields.map((f) => (
              <Field
                key={f.name}
                {...f}
                value={form[f.name]}
                onChange={handleChange}
              />
            ))}
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t border-blue-50">
            <button
              onClick={closeModal}
              className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm text-slate-500 hover:text-slate-700 border border-slate-200 hover:bg-slate-50 transition"
            >
              <RiCloseLine className="w-4 h-4" /> Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={actionLoading}
              className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white transition shadow-md shadow-blue-200 disabled:opacity-60"
            >
              {actionLoading ? (
                <Spinner />
              ) : (
                <RiCheckLine className="w-4 h-4" />
              )}
              Create Branch
            </button>
          </div>
        </Modal>
      )}

      {/* ── Edit Modal ── */}
      {modal === "edit" && (
        <Modal
          title={
            <>
              <RiEditLine className="w-5 h-5 text-blue-500" /> Edit —{" "}
              {selected?.branchName}
            </>
          }
          onClose={closeModal}
        >
          {/* Bank selector at top */}
          <div className="flex flex-col gap-1.5 mb-4">
            <label className="text-xs font-semibold text-blue-700 uppercase tracking-widest">
              Select Bank
            </label>
            <BankDropdown
              banks={banks}
              value={form.bankId}
              onChange={(id) => setForm((f) => ({ ...f, bankId: id }))}
              loading={banksLoading}
            />
          </div>

          <div className="grid grid-cols-2 gap-4 mb-6">
            {formFields.map((f) => (
              <Field
                key={f.name}
                {...f}
                value={form[f.name]}
                onChange={handleChange}
              />
            ))}
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t border-blue-50">
            <button
              onClick={closeModal}
              className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm text-slate-500 hover:text-slate-700 border border-slate-200 hover:bg-slate-50 transition"
            >
              <RiCloseLine className="w-4 h-4" /> Cancel
            </button>
            <button
              onClick={handleUpdate}
              disabled={actionLoading}
              className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white transition shadow-md shadow-blue-200 disabled:opacity-60"
            >
              {actionLoading ? (
                <Spinner />
              ) : (
                <RiCheckLine className="w-4 h-4" />
              )}
              Save Changes
            </button>
          </div>
        </Modal>
      )}

      {/* ── Delete Modal ── */}
      {modal === "delete" && (
        <Modal
          title={
            <>
              <RiDeleteBinLine className="w-5 h-5 text-red-500" /> Confirm
              Deletion
            </>
          }
          onClose={closeModal}
        >
          <div className="text-center py-4">
            <div className="w-16 h-16 rounded-full bg-red-50 border border-red-100 flex items-center justify-center mx-auto mb-4">
              <RiErrorWarningLine className="w-8 h-8 text-red-500" />
            </div>
            <p className="text-slate-700 font-semibold mb-1 text-base">
              Delete{" "}
              <span className="text-red-500">{selected?.branchName}</span>?
            </p>
            <p className="text-slate-400 text-sm mb-6">
              This action cannot be undone. All data for this branch will be
              permanently removed.
            </p>
            <div className="flex justify-center gap-3">
              <button
                onClick={closeModal}
                className="flex items-center gap-1.5 px-6 py-2 rounded-lg text-sm text-slate-500 border border-slate-200 hover:bg-slate-50 transition"
              >
                <RiCloseLine className="w-4 h-4" /> Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={actionLoading}
                className="flex items-center gap-2 px-6 py-2 rounded-lg text-sm font-semibold bg-red-500 hover:bg-red-600 text-white transition shadow-md shadow-red-100 disabled:opacity-60"
              >
                {actionLoading ? (
                  <Spinner />
                ) : (
                  <RiDeleteBinLine className="w-4 h-4" />
                )}
                Delete Branch
              </button>
            </div>
          </div>
        </Modal>
      )}

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
}
